// ADDED
/**
 * OffsetLeavePicker - Wrapper around generic EntityPicker
 * 
 * Provides configuration for selecting Leave references (Ref No/Type/Days)
 * used in Offset Requests.
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── Leave-specific configuration ─────────────────────────────────────────────
const get_leave_config = (employeeId) => ({
  apiEndpoint: `/api/OffsetLeave/employee/${employeeId}`,
  entityName: "Leave Reference",
  idField: "id",
  displayFields: {
    primary: "leaveData.referenceNumber",
    secondary: "remainingValue"
  },
  tableHeaders: ["Ref No.", "Type", "Days", "Remaining"],
  tableAccessors: ["leaveData.referenceNumber", "leaveData.leaveTypeDescription", "leaveData.days", "remainingValue"],
  searchFields: [
    { label: "All", field: "all", index: 0 },
    { label: "Ref No.", field: "leaveData.referenceNumber", index: 1 },
  ],
  placeholder: "--Select Leave Reference--"
});

// ── OffsetLeavePickerField wrapper ───────────────────────────────────────────
/**
 * OffsetLeavePickerField
 * 
 * Usage:
 * <OffsetLeavePickerField
 *   label="Reference ID:"
 *   employeeId={formData.employeeId}
 *   referenceId={formData.reference?.id}
 *   displayValue={formData.reference?.leaveData?.referenceNumber}
 *   onSelect={(leaveRef) => handleInputChange('reference', leaveRef)}
 *   onClear={() => handleInputChange('reference', null)}
 *   error={errorState && !formData.reference}
 * />
 */
export function OffsetLeavePickerField({
  employeeId,
  referenceId, // Maps to entityId
  displayValue = "",
  onSelect,
  onClear,
  disabled,
  ...rest       // label, isRequired, error, etc.
}) {
  const config = get_leave_config(employeeId);

  // Format display: OS-000001
  const formattedDisplay = displayValue
    ? `OS-${displayValue.toString().padStart(6, '0')}`
    : "";

  return (
    <EntityPickerField
      {...rest}
      config={config}
      entityId={referenceId}
      displayValue={formattedDisplay}
      onSelect={onSelect}
      onClear={onClear}
      disabled={disabled || !employeeId} // Lock picker if no employee is selected
    />
  );
}

// ── OffsetLeavePickerModal wrapper ───────────────────────────────────────────
/**
 * OffsetLeavePickerModal
 */
export function OffsetLeavePickerModal({ isOpen, onClose, setData, employeeId }) {
  const config = get_leave_config(employeeId);
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={config}
    />
  );
}

export { OffsetLeavePickerField as default };